import { getISOWeek } from "date-fns";
import { formattedWasteColor } from "../../../components/formatted/FormattedText";
/**
 * Utilitaires pour les graphiques de statistiques de collection
 */

const weekCategoryLabel = (time) => {
  const date = new Date(time);
  if (Number.isNaN(date.getTime())) return String(time);
  return `S${getISOWeek(date)}`;
};

/**
 * One category per week, in chronological order.
 * Equal slots keep the bars spread across the full chart width.
 * The year is added only when the same week number appears twice.
 */
const buildWeekCategories = (times) => {
  const sortedTimes = [...times].sort((a, b) => new Date(a) - new Date(b));
  const entries = sortedTimes.map((time) => {
    const date = new Date(time);
    const valid = !Number.isNaN(date.getTime());
    return {
      time: String(time),
      week: valid ? getISOWeek(date) : null,
      year: valid ? date.getFullYear() : null,
      label: weekCategoryLabel(time),
    };
  });
  const counts = new Map();
  entries.forEach((entry) => {
    counts.set(entry.label, (counts.get(entry.label) || 0) + 1);
  });
  entries.forEach((entry) => {
    if ((counts.get(entry.label) || 0) > 1 && entry.year != null) {
      entry.label = `S${entry.week} ${entry.year}`;
    }
  });
  return entries;
};

/**
 * Construit les datasets pour le graphique de rotations par flux
 */
export const buildCollectionByFluxChartData = (data, periodMode = "month") => {
  const dataSetsCollectionByFluxChart = [];
  const labelsByTimeChart = [];
  const isWeek = periodMode === "week";

  const distributionKey = isWeek ? "weeklyDistribution" : "monthlyDistribution";

  Object.keys(data).forEach((flux) => {
    const distribution = data[flux][distributionKey] || [];

    distribution.forEach((item) => {
      if (!labelsByTimeChart.includes(item.time)) {
        labelsByTimeChart.push(item.time);
      }
    });
  });

  const weekCategories = isWeek ? buildWeekCategories(labelsByTimeChart) : [];
  const weekLabelByTime = new Map(
    weekCategories.map((entry) => [entry.time, entry.label]),
  );
  const categoryLabels = weekCategories.map((entry) => entry.label);

  Object.keys(data).forEach((flux) => {
    const distribution = data[flux][distributionKey] || [];

    dataSetsCollectionByFluxChart.push({
      label: flux,
      data: distribution.map((item) => ({
        x: isWeek
          ? weekLabelByTime.get(String(item.time)) ||
            weekCategoryLabel(item.time)
          : item.time,
        y: item.countCollection,
        z: flux,
      })),
      backgroundColor: formattedWasteColor(flux),
      type: "bar",
    });
  });

  return {
    dataSetsCollectionByFluxChart,
    labelsByTimeChart: isWeek ? categoryLabels : labelsByTimeChart,
  };
};

export const buildFillingLevelGroupedBarChartData = (data, t) => {
  const labelsByFluxChart = Object.keys(data);

  const dataSetsFillingLevelGroupedBar = [
    {
      label: t("Minimum"),
      data: labelsByFluxChart.map((flux) => ({
        x: flux,
        y: data[flux].min,
        z: "Minimum",
      })),
      backgroundColor: "#C5E8E8",
      type: "bar",
    },
    {
      label: t("Average"),
      data: labelsByFluxChart.map((flux) => ({
        x: flux,
        y: data[flux].sumFillingLevel / data[flux].countFillingLevel,
        z: t("Average"),
      })),
      backgroundColor: "#00A7A6",
      type: "bar",
    },
    {
      label: t("Maximum"),
      data: labelsByFluxChart.map((flux) => ({
        x: flux,
        y: data[flux].max,
        z: "Max",
      })),
      backgroundColor: "#000000",
      type: "bar",
    },
  ];

  return { dataSetsFillingLevelGroupedBar, labelsByFluxChart };
};

export const buildFillingLevelByTimeChartData = (data) => {
  const labelsByTimeChart = [];
  const dataSetsFillingLevelByTime = Object.keys(data).map((flux) => {
    const color = formattedWasteColor(flux);
    const distribution = data[flux].monthlyDistribution || [];

    distribution.forEach(({ time }) => {
      if (!labelsByTimeChart.includes(time)) labelsByTimeChart.push(time);
    });

    return {
      label: flux,
      data: distribution
        .filter((item) => item.countFillingLevel > 0)
        .map((item) => ({
          x: item.time,
          y: item.sumFillingLevel / item.countFillingLevel,
          z: flux,
        })),
      backgroundColor: color,
      borderColor: color,
      type: "line",
      tension: 0.2,
    };
  });

  return { dataSetsFillingLevelByTime, labelsByTimeChart };
};

export const formatAveragePackmatRate = (value) => {
  if (value == null || Number.isNaN(Number(value))) return "—";
  return Number(value).toLocaleString(undefined, {
    minimumFractionDigits: 1,
    maximumFractionDigits: 1,
  });
};

export const buildTrendByWasteCategory = (
  dataResult = {},
  packmatPerformance,
) => {
  if (!dataResult || typeof dataResult !== "object") return [];

  return Object.keys(dataResult)
    .map((item) => {
      const { sumFillingLevel, countFillingLevel, countPackmat } =
        dataResult[item] || {};
      const packmatCount = Number(countPackmat) || 0;

      const packmatData =
        packmatPerformance?.packmatPerWasteCategory?.[item] || {};

      const averageFillRate =
        countFillingLevel > 0 ? sumFillingLevel / countFillingLevel : 0;

      const averageGain =
        packmatData.packmatCount > 0 ? packmatData.gain / countFillingLevel : 0;

      const performance = averageFillRate + averageGain;

      return {
        wasteCategory: item,
        rotations: countFillingLevel,
        averageGain: Number(averageGain.toFixed(2)),
        performance: Number(performance.toFixed(2)),
        factor: Number((performance / 100).toFixed(2)),
        averageFillRate: Number(averageFillRate.toFixed(2)),
        averagePackmatRate:
          countFillingLevel > 0 ? packmatCount / countFillingLevel : null,
      };
    })
    .sort((left, right) => right.rotations - left.rotations);
};

/**
 * Calcule le ranking d'une catégorie de déchets (up, down, stable)
 */
export const findRanking = (
  wasteCategory,
  rotations,
  trendByWasteCategoryPrevious,
) => {
  const result = trendByWasteCategoryPrevious.filter(
    (item) => item.wasteCategory === wasteCategory,
  )[0];
  if (result === undefined) {
    return "up";
  } else if (rotations > result.rotations) {
    return "up";
  } else if (rotations < result.rotations) {
    return "down";
  } else {
    return "stable";
  }
};
